import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { AgentRunEvent, StrategyRunRow } from '../services/strategyApi'
import { isToolCall, useAgentRunEvents } from './useAgentRunEvents'

// One Strategy run, as its run log shows it: how it ended, its summary, and its timeline.

export type RunLogTone = 'ok' | 'warn' | 'danger' | 'info'

export interface RunLogEntry {
  id: string
  at: string | null
  kind: 'progress' | 'tool' | 'note' | 'error'
  text: string
}

export interface RunLogView {
  status: string
  tone: RunLogTone
  summary: string
  timeline: RunLogEntry[]
}

export const RUN_STATUS_LABELS: Readonly<Record<string, string>> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Finished',
  failed: 'Failed',
  stopped: 'Stopped',
  cancelled: 'Stopped',
}

const RUN_STATUS_TONES: Readonly<Record<string, RunLogTone>> = { queued: 'info', running: 'info', succeeded: 'ok', failed: 'danger' }

export function runStatus(status: string): { status: string; tone: RunLogTone } {
  return { status: RUN_STATUS_LABELS[status] ?? 'Stopped', tone: RUN_STATUS_TONES[status] ?? 'warn' }
}

function entryOf(e: AgentRunEvent): RunLogEntry | null {
  const base = { id: e.id, at: e.created_at }
  if (e.kind === 'progress' && e.message) return { ...base, kind: 'progress', text: e.message }
  if (e.kind === 'tool') {
    if (!isToolCall(e)) return null
    const failed = e.payload?.tool?.phase === 'error'
    return { ...base, kind: failed ? 'error' : 'tool', text: e.message || String(e.payload?.tool?.name ?? 'Tool call') }
  }
  if (!e.message) return null
  return { ...base, kind: e.payload?.status === 'failed' ? 'error' : 'note', text: e.message }
}

export function runLogView(run: StrategyRunRow, events: readonly AgentRunEvent[]): RunLogView {
  return {
    ...runStatus(run.status),
    summary: run.summary,
    timeline: events.map(entryOf).filter((e): e is RunLogEntry => e !== null),
  }
}

export function useStrategyRunLog(run: MaybeRefOrGetter<StrategyRunRow | null>) {
  const { events } = useAgentRunEvents(() => toValue(run)?.id ?? null)
  const view = computed(() => {
    const r = toValue(run)
    return r && runLogView(r, events.value)
  })
  return { view }
}
